/**
 * InstallPrompt Component
 *
 * Shows a banner prompting users to install the PWA on their device.
 * Listens for the beforeinstallprompt event and provides install functionality.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';

/**
 * BeforeInstallPromptEvent interface
 * Extended Event interface for PWA install prompt
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

/**
 * Props for InstallPrompt component
 */
export interface InstallPromptProps {
  /** Callback when install is successful */
  onInstallSuccess?: () => void;
  /** Callback when install is dismissed */
  onDismiss?: () => void;
}

/**
 * InstallPrompt displays a banner to prompt PWA installation
 *
 * @example
 * ```tsx
 * <InstallPrompt
 *   onInstallSuccess={() => console.log('Installed!')}
 *   onDismiss={() => console.log('Dismissed')}
 * />
 * ```
 */
export const InstallPrompt = ({ onInstallSuccess, onDismiss }: InstallPromptProps) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  /**
   * Check if prompt was previously dismissed
   */
  const wasDismissed = useCallback((): boolean => {
    try {
      const dismissedAt = localStorage.getItem('pwa-install-dismissed');
      if (!dismissedAt) return false;

      // Don't show again for 7 days after dismissal
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissal = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceDismissal < 7;
    } catch {
      return false;
    }
  }, []);

  /**
   * Check if app is already installed
   */
  const isInstalled = useCallback((): boolean => {
    // Check if running in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // Check for iOS standalone mode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    return false;
  }, []);

  /**
   * Handle beforeinstallprompt event
   */
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      event.preventDefault();

      // Don't show if already dismissed recently or already installed
      if (wasDismissed() || isInstalled()) {
        return;
      }

      // Store the event for later use
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    const handleAppInstalled = () => {
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
      onInstallSuccess?.();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [wasDismissed, isInstalled, onInstallSuccess]);

  /**
   * Handle install button click
   */
  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        // User accepted - the appinstalled event will fire
        setShowPrompt(false);
      } else {
        // User dismissed
        setIsInstalling(false);
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error prompting PWA install:', error);
      setIsInstalling(false);
    }
  };

  /**
   * Handle dismiss button click
   */
  const handleDismiss = () => {
    try {
      localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    } catch {
      // Ignore localStorage errors
    }

    setShowPrompt(false);
    setDeferredPrompt(null);
    onDismiss?.();
  };

  // Don't render if no prompt available or already installed
  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div style={containerStyle} role="alert" aria-live="polite">
      <div style={contentStyle}>
        <div style={iconContainerStyle}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 4L12 14M12 14L8 10M12 14L16 10"
              stroke="#2563eb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17"
              stroke="#2563eb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div style={textContainerStyle}>
          <h3 style={titleStyle}>Install Flash</h3>
          <p style={descriptionStyle}>
            Add to your home screen for quick access and offline support
          </p>
        </div>

        <div style={actionsStyle}>
          <button
            type="button"
            onClick={handleInstall}
            disabled={isInstalling}
            style={installButtonStyle}
            aria-label="Install Flash app"
          >
            {isInstalling ? 'Installing...' : 'Install'}
          </button>

          <button
            type="button"
            onClick={handleDismiss}
            style={dismissButtonStyle}
            aria-label="Dismiss install prompt"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'var(--color-bg-primary)',
  borderTop: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-lg)',
  zIndex: 9999,
  padding: '16px',
};

const contentStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  maxWidth: '600px',
  margin: '0 auto',
  flexWrap: 'wrap',
};

const iconContainerStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  backgroundColor: 'var(--color-bg-tertiary)',
  borderRadius: '12px',
};

const textContainerStyle: CSSProperties = {
  flex: 1,
  minWidth: '150px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const descriptionStyle: CSSProperties = {
  margin: '4px 0 0 0',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  flexShrink: 0,
};

const installButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const dismissButtonStyle: CSSProperties = {
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

export default InstallPrompt;
